import { environment } from 'src/environments/environment';

export interface IUserInfo {
  imageUrl: string
  // The licence's own logo for the top bar (public URL); null or missing = Evide's logo.
  licenseLogoUrl?: string | null;
  licenseId: never;
  name: string;
  projectId: never;
  surname: string;
  userId: number;
  validTo: string;
  isFirstLogin: boolean;
  helplineEnabled: boolean;
  nonCaseLoadEnabled: boolean;
  actionPlanEnabled: boolean;
  showChooseServiceOnContactCreate: boolean;
  riskAlertsEnabled: boolean;
  propertiesEnabled: boolean;
  // Support Plan module (needs Action Plans): the tab shows when supportPlanEnabled AND the project
  // has picked its assessment questionnaire / objective template on the Update Project page.
  supportPlanEnabled: boolean;
  // SMS messaging switch for the current project (the Sms/Status call says whether it is also ready).
  smsEnabled?: boolean;
  supportAssessmentFormGuid: string | null;
  supportObjectiveTemplateFormGuid: string | null;
  // Referral workflow: Refer to Service / Accept / Decline offered only when true.
  referralWorkflowEnabled: boolean;
  // Duration sessions: "New Duration Session" entry points shown only when true.
  durationSessionsEnabled: boolean;
  // Therapy approval workflow: adding a client to an activity with Allocated Sessions goes
  // through a request for the roles ticked on the Therapy Activity Requested trigger.
  therapyApprovalWorkflowEnabled: boolean;
  workflowEventsEnabled: boolean;
  // Legacy-module switches — default TRUE (the modules predate their switches).
  familyEnabled: boolean;
  organisationEnabled: boolean;
  waitingListEnabled: boolean;
  isSystemAdmin: boolean;
  isProgramAdminOrAbove: boolean;
  isProjectAdminOrAbove: boolean;
  isActive: boolean;
}

export interface IAzureAccountInfo {
  name: string;
  username: string;
}
export class LoginUser {
  public firstName = '';
  public lastName = '';
  public userId: number;
  public licenseId: number;
  public projectId: number;
  public fullName: string;
  public imageUrl: string;
  public licenseLogoUrl: string | null = null;
  public isFirstLogin: boolean;
  public helplineEnabled = false;
  public nonCaseLoadEnabled = false;
  public actionPlanEnabled = false;
  public showChooseServiceOnContactCreate = false;
  public riskAlertsEnabled = false;
  public propertiesEnabled = false;
  // The Support Plan tab shows only when supportPlanEnabled && supportAssessmentFormGuid.
  public supportPlanEnabled = false;
  public smsEnabled = false;
  public supportAssessmentFormGuid: string | null = null;
  public supportObjectiveTemplateFormGuid: string | null = null;
  public referralWorkflowEnabled = false;
  public durationSessionsEnabled = false;
  public therapyApprovalWorkflowEnabled = false;
  public workflowEventsEnabled = false;
  // Legacy modules default ON — opposite polarity to the toggles above.
  public familyEnabled = true;
  public organisationEnabled = true;
  public waitingListEnabled = true;
  public isSystemAdmin = false;
  public isProgramAdminOrAbove = false;
  public isProjectAdminOrAbove = false;

  constructor(
    public username: string,
    public token: string,
    public accountName: string
  ) { }

  public static adaptAzure(token: string, account: IAzureAccountInfo) {
    return new this(
      account.username,
      token,
      account.name
    );
  }

  public static setUserInfo(user: LoginUser, userInfo: IUserInfo) {
    user.firstName = userInfo.name;
    user.lastName = userInfo.surname;
    user.projectId = userInfo.projectId;
    user.licenseId = userInfo.licenseId;
    user.userId = userInfo.userId;
    user.fullName = `${user.firstName} ${user.lastName}`;
    user.imageUrl = userInfo.imageUrl;
    user.licenseLogoUrl = userInfo.licenseLogoUrl || null;
    user.isFirstLogin = userInfo.isFirstLogin;
    user.helplineEnabled = !!userInfo.helplineEnabled;
    user.nonCaseLoadEnabled = !!userInfo.nonCaseLoadEnabled;
    user.actionPlanEnabled = !!userInfo.actionPlanEnabled;
    user.showChooseServiceOnContactCreate = !!userInfo.showChooseServiceOnContactCreate;
    user.riskAlertsEnabled = !!userInfo.riskAlertsEnabled;
    user.propertiesEnabled = !!userInfo.propertiesEnabled;
    user.supportPlanEnabled = !!userInfo.supportPlanEnabled;
    user.smsEnabled = !!userInfo.smsEnabled;
    user.supportAssessmentFormGuid = userInfo.supportAssessmentFormGuid || null;
    user.supportObjectiveTemplateFormGuid = userInfo.supportObjectiveTemplateFormGuid || null;
    user.referralWorkflowEnabled = !!userInfo.referralWorkflowEnabled;
    user.durationSessionsEnabled = !!userInfo.durationSessionsEnabled;
    user.therapyApprovalWorkflowEnabled = !!userInfo.therapyApprovalWorkflowEnabled;
    user.workflowEventsEnabled = !!userInfo.workflowEventsEnabled;
    // !== false (not !!): a payload from an API without these fields must read as ON —
    // hiding live modules because a field was missing is the failure mode we must never have.
    user.familyEnabled = userInfo.familyEnabled !== false;
    user.organisationEnabled = userInfo.organisationEnabled !== false;
    user.waitingListEnabled = userInfo.waitingListEnabled !== false;
    user.isSystemAdmin = !!userInfo.isSystemAdmin;
    user.isProgramAdminOrAbove = !!userInfo.isProgramAdminOrAbove;
    user.isProjectAdminOrAbove = !!userInfo.isProjectAdminOrAbove;
  }

  static prepareUrl(url: string): string {
    return url ? environment.imgUrl + url : ''
  }

}
